import { useMutation, useQueryClient } from '@tanstack/react-query';
import useHttpClient from './useHttpClient';

export interface IProjectContactDataReq {
  orgContactId: {
    native: number;
  };
}

export interface IProjectContactDataRes {
  projectId: {
    native: number;
  };
  projectContactId: {
    native: number;
  };
}

export interface IContactPayload {
  addProjectContact: (
    projectId: number,
    payload: IProjectContactDataReq,
  ) => Promise<IProjectContactDataRes>;
  loading: boolean;
  error?: Error;
  called: boolean;
  contact?: IProjectContactDataRes;
}

const useCreateProjectContact = (): IContactPayload => {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { mutateAsync, error, isPending, isSuccess, isError, data } = useMutation({
    mutationFn: async ({ projectId, contactData }: { projectId: number; contactData: IProjectContactDataReq }) => {
      const response = await httpClient.post(`/projects/${projectId}/contacts`, contactData);
      return response.data as IProjectContactDataRes;
    },
    onSuccess: (result, variables) => {
      // Invalidate project contacts queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['projectContacts', variables.projectId] });
    },
  });

  const addProjectContact = async (projectId: number, contactData: IProjectContactDataReq) => {
    const result = await mutateAsync({ projectId, contactData });
    return result;
  };

  return {
    addProjectContact,
    error: error as Error,
    loading: isPending,
    called: isSuccess || isError,
    contact: data,
  };
};

export default useCreateProjectContact;
